import React from "react";
import { withTranslation } from "react-i18next";
import classNames from "classnames";
import "./index.scss";
import {Popover} from 'antd';

import { ORDER_NOTE_EDIT } from "../../../store/reducers/order/order-actions";

import { INTEGRATION_TYPE, ORDER_STATE, WAITER_TYPES } from '../../../utils/enums';

import ConfirmDialog from "../../containers/ConfirmDialogContainer";
import Waiter from "../Waiter/Waiter";
import IconButton from "../IconButton";
import CommentEditor from "../CommentEditor";
import {isRTL} from "../../../services/userModel.js";
import CompensationEditor from '../CompensationEditor/index.js';

const EditButtonsBlock = ({
  t,
  id,
  orderState,
  integrationType,
  serviceNotes,
  showConfirmDialog,
  isWithPassword,
  isAssistanceOrder,
  courierConfirmDialogType,
  handleOnConfirmDialog,
  handleOnCancelConfirmDialog,
  handleOnEdit,
  handleOnDeleteClick,
  handleOnPreviewClick,
  handleOnRevertClick,
  handleOnChangeOrderState,
  showCompensation,
  courierCompensation,
  withLabels,
}) => {
  const isDeliveredCanceled = orderState === ORDER_STATE.delivered || orderState === ORDER_STATE.canceled;
  // Ordering (marketplace) orders can't be deleted here — they go through Customer Service.
  const isOrderingIntegration = integrationType === INTEGRATION_TYPE.ordering;

  // withLabels (the mobile "⋯" context menu): every action renders as a full-width menu row —
  // icon + caption — since touch has no hover tooltips to explain a bare icon. The icons keep
  // their own click handlers (they stopPropagation), the row itself fires the same action so
  // tapping the caption works too; rows without an action (self-contained editors) just label.
  const row = (label, node, onClick) => !withLabels ? node : (
    <div className="ebbRow" onClick={onClick} key={label}>
      {node}
      <span className="ebbLabel">{label}</span>
    </div>
  );

  return (
    <div className={classNames("editButtonsBlock", { withLabels })}>
      <div className="edit-buttons">
        {!isDeliveredCanceled && orderState === ORDER_STATE.onroute && !isAssistanceOrder && row(
          t("orders.ORDER_DELIVERY_TOOLTIP"),
          <Waiter type={WAITER_TYPES.buttonSvg}
            useClass="deliver"
            useSvg="#icon-arrow-right"
            stopPropagation={true}
            tooltipText={t("orders.ORDER_DELIVERY_TOOLTIP")}
            handleOnClick={()=> new Promise(resolve=> { resolve(); handleOnChangeOrderState(ORDER_STATE.delivered); })}
          />,
          () => handleOnChangeOrderState(ORDER_STATE.delivered)
        )}
        {!isDeliveredCanceled && row(
          t("orders.ORDER_EDIT_TOOLTIP"),
          <IconButton isIcon={true} iconName={"orderEdit"} tooltipText={t("orders.ORDER_EDIT_TOOLTIP")}
            onClick={()=> new Promise(resolve=> { resolve(); handleOnEdit(); })}
          />,
          () => handleOnEdit()
        )}

        {!isDeliveredCanceled && !isAssistanceOrder && row(
          t(isOrderingIntegration ? "orders.ORDER_DISABLE_DELETE_TOOLTIP" : "orders.ORDER_DELETE_TOOLTIP"),
            <Popover
                placement={isRTL() ? "right" : "left"}
                rootClassName={"confirmDialogPopoverWrapper"} open={showConfirmDialog && courierConfirmDialogType !== null} content={
              <ConfirmDialog isWithPassword={isWithPassword}
                             isCancelFirst={true}
                             useWrapperClass={classNames("confirmDialogPopover")}
                             useBtnConfirmClass={classNames("btn confirm btnConfirmDialog")}
                             handleOnConfirm={handleOnConfirmDialog}
                             handleOnCancel={handleOnCancelConfirmDialog}
              />
            }>
              <IconButton disabled={isOrderingIntegration} isIcon={true} iconName={"delete"} tooltipText={t(isOrderingIntegration ? "orders.ORDER_DISABLE_DELETE_TOOLTIP" : "orders.ORDER_DELETE_TOOLTIP")}
                          onClick={()=> new Promise(resolve=> { resolve(); handleOnDeleteClick(); })}
              />
            </Popover>,
          isOrderingIntegration ? undefined : () => handleOnDeleteClick()
        )}
        {isDeliveredCanceled && !isAssistanceOrder && row(
          t("orders.ORDER_PREVIEW_TOOLTIP"),
          <IconButton isIcon={true} iconName={"see"} tooltipText={t("orders.ORDER_PREVIEW_TOOLTIP")}
            onClick={handleOnPreviewClick}
          />,
          () => handleOnPreviewClick()
        )}
        {isDeliveredCanceled && !isAssistanceOrder && row(
          t(isOrderingIntegration ? "orders.ORDER_DISABLE_DELETE_TOOLTIP" : "orders.ORDER_DELETE_TOOLTIP"),
            <Popover
                placement={isRTL() ? "right" : "left"}
                rootClassName={"confirmDialogPopoverWrapper"} open={showConfirmDialog && courierConfirmDialogType !== null} content={
              <ConfirmDialog isWithPassword={isWithPassword}
                             isCancelFirst={true}
                             useWrapperClass={classNames("confirmDialogPopover")}
                             useBtnConfirmClass={classNames("btn confirm btnConfirmDialog")}
                             handleOnConfirm={handleOnConfirmDialog}
                             handleOnCancel={handleOnCancelConfirmDialog}
              />
            }>
              <IconButton isIcon={true} disabled={isOrderingIntegration} iconName={"delete"} tooltipText={t(isOrderingIntegration ? "orders.ORDER_DISABLE_DELETE_TOOLTIP" : "orders.ORDER_DELETE_TOOLTIP")}
                          onClick={()=> new Promise(resolve=> { resolve(); handleOnDeleteClick(); })}
              />
            </Popover>,
          isOrderingIntegration ? undefined : () => handleOnDeleteClick()
        )}
        {isDeliveredCanceled && !isAssistanceOrder && row(
          t("orders.ORDER_ACTIVE_TOOLTIP"),
          <IconButton isIcon={true} iconName={"repeat"} tooltipText={t("orders.ORDER_ACTIVE_TOOLTIP")}
            onClick={()=> new Promise(resolve=> { resolve(); handleOnRevertClick(); })}
          />,
          () => handleOnRevertClick()
        )}
        {!isAssistanceOrder && row(
          serviceNotes ? t("orders.ORDER_NOTE_EDIT_TOOLTIP") : t("orders.ORDER_NOTE_CREATE_TOOLTIP"),
          <CommentEditor id={id} value={serviceNotes} action={ORDER_NOTE_EDIT}
            tooltipText={serviceNotes ? t("orders.ORDER_NOTE_EDIT_TOOLTIP") : t("orders.ORDER_NOTE_CREATE_TOOLTIP")}
            placeholder={t("orders.ORDER_NOTE_PLACEHOLDER")}
          />
        )}
        {showCompensation && row(
          t("orders.ORDER_COMPENSATION_TOOLTIP"),
          <CompensationEditor id={id} defaultValue={courierCompensation ? { compensation: courierCompensation, notes: '' } : null} />
        )}
      </div>
    </div>
  );
}

export default withTranslation()(EditButtonsBlock);
